import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSection from './FAQSection';
import './HealthcarePage.css';

const HealthcarePage = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1 });
  const [benefitsRef, benefitsInView] = useInView({ threshold: 0.1 });
  const [processRef, processInView] = useInView({ threshold: 0.1 });

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  // const slideInRight = {
  //   hidden: { opacity: 0, x: 50 },
  //   visible: {
  //     opacity: 1,
  //     x: 0,
  //     transition: { duration: 0.8, ease: "easeOut" }
  //   }
  // };

  const keyFeatures = [
    {
      icon: "bi-heart-pulse",
      title: "Patient Management & Records",
      description: "Comprehensive patient data management with secure electronic health records and HIPAA compliance.",
      color: "#FF7A00"
    },
    {
      icon: "bi-calendar2-check",
      title: "Appointment Scheduling",
      description: "Advanced scheduling system with automated reminders and resource optimization for medical facilities.",
      color: "#0066CC"
    },
    {
      icon: "bi-receipt",
      title: "Billing & Revenue Management",
      description: "Streamlined medical billing, insurance claims processing, and revenue cycle management.",
      color: "#FF7A00"
    },
    {
      icon: "bi-clipboard-data",
      title: "Compliance & Reporting",
      description: "Built-in compliance tools for healthcare regulations and comprehensive reporting for decision making.",
      color: "#0066CC"
    },
    {
      icon: "bi-graph-up-arrow",
      title: "Analytics & Performance Tracking",
      description: "Real-time healthcare analytics to track patient outcomes, operational efficiency, and financial performance.",
      color: "#FF7A00"
    },
    {
      icon: "bi-shield-check",
      title: "Security & Data Protection",
      description: "Enterprise-grade security measures ensuring patient data protection and regulatory compliance.",
      color: "#0066CC"
    }
  ];

  const benefits = [
    {
      icon: "bi-hospital",
      title: "Hospitals & Health Systems",
      description: "Streamline operations across multiple departments with integrated patient care management.",
      stats: "50% improved efficiency"
    },
    {
      icon: "bi-person-hearts",
      title: "Medical Practices",
      description: "Optimize practice management with comprehensive patient scheduling and billing solutions.",
      stats: "40% faster billing"
    },
    {
      icon: "bi-heart",
      title: "Specialty Clinics",
      description: "Specialized workflows for cardiology, oncology, and other specialty healthcare providers.",
      stats: "35% better outcomes"
    },
    {
      icon: "bi-capsule",
      title: "Pharmaceutical Companies",
      description: "Manage drug development, compliance, and distribution with integrated supply chain solutions.",
      stats: "60% compliance improvement"
    },
    {
      icon: "bi-bandaid",
      title: "Home Healthcare Services",
      description: "Coordinate care delivery, scheduling, and billing for home healthcare providers.",
      stats: "45% cost reduction"
    },
    {
      icon: "bi-activity",
      title: "Medical Device Companies",
      description: "Track inventory, manage warranties, and ensure regulatory compliance for medical equipment.",
      stats: "30% inventory optimization"
    }
  ];

  const processSteps = [
    {
      title: "Enhanced Patient Care",
      description: "Improve patient outcomes with integrated care coordination and data-driven insights",
      icon: "bi-heart-pulse-fill"
    },
    {
      title: "Operational Efficiency",
      description: "Streamline healthcare operations and reduce administrative overhead costs",
      icon: "bi-speedometer2"
    },
    {
      title: "Regulatory Compliance",
      description: "Ensure full compliance with healthcare regulations and maintain audit readiness",
      icon: "bi-shield-fill-check"
    },
    {
      title: "Financial Optimization",
      description: "Optimize revenue cycles and reduce claim denials with intelligent billing systems",
      icon: "bi-graph-up"
    }
  ];

  return (
    <div className="healthcare-page">
      <Helmet>
        <title>NetSuite for Healthcare Services USA | SpireNest</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/industries/healthcare" />
        <meta name="description" content="Improve healthcare operations with SpireNest's NetSuite solutions, ensuring compliance and efficiency for U.S. healthcare providers." />
        <meta name="keywords" content="NetSuite healthcare, healthcare ERP, medical billing, patient management, USA, SpireNest" />
        <meta property="og:title" content="NetSuite for Healthcare Services | SpireNest USA" />
        <meta property="og:description" content="Simplify healthcare operations with NetSuite ERP tailored for medical and health service providers." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spirenest.co/industries/healthcare-services" />
        <meta property="og:image" content="https://spirenest.co/assets/images/og-healthcare.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NetSuite for Healthcare Services | SpireNest USA" />
        <meta name="twitter:description" content="Improve care delivery and administration with industry-specific ERP tools." />
        <meta name="twitter:image" content="https://spirenest.co/assets/images/og-healthcare.jpg" />
      </Helmet>
      {/* Hero Section */}
      <section className="hero-section position-relative overflow-hidden">
        <div className="hero-bg-overlay"></div>
        <div className="floating-shapes">
          <motion.div className="shape-1" animate={{ rotate: 360, y: [-20, 20] }} transition={{ duration: 20, repeat: Infinity }} />
          <motion.div className="shape-2" animate={{ rotate: -360, x: [-30, 30] }} transition={{ duration: 25, repeat: Infinity }} />
          <motion.div className="shape-3" animate={{ y: [-15, 15], rotate: 180 }} transition={{ duration: 15, repeat: Infinity }} />
        </div>
        
        <Container className="position-relative">
          <Row className="align-items-center min-vh-100">
            <Col lg={7}>
              <motion.div
                ref={heroRef}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                variants={staggerContainer}
                className="hero-content"
              >
                <motion.div variants={fadeInUp}>
                  <Badge className="hero-badge mb-4">
                    <i className="bi-heart-pulse me-2"></i>
                    HEALTHCARE 
                  </Badge>
                </motion.div>
                
                <motion.h1 variants={slideInLeft} className="hero-title">
                  NetSuite ERP for <span style={{background: 'linear-gradient(135deg, #FF8F00, #FF6B35)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>Healthcare </span>
                </motion.h1>
                
                <motion.p variants={slideInLeft} className="hero-description">
                  Transform your healthcare operations with our comprehensive NetSuite ERP solution designed 
                  specifically for the US healthcare industry. Streamline patient management, ensure regulatory 
                  compliance, optimize billing processes, and enhance care delivery across all healthcare settings. 
                  From hospitals to specialty clinics, manage your entire healthcare organization with precision and security.
                </motion.p>
              </motion.div>
            </Col>
            
            <Col lg={5}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8, rotateY: 45 }}
                animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="hero-visual"
              >
                <div className="healthcare-visual">
                  <motion.div 
                    className="medical-container"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                  >
                    <i className="bi-hospital medical-icon" style={{color: '#FF8F00'}}></i>
                  </motion.div>
                  <motion.div 
                    className="heart-small-1"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <i className="bi-heart-pulse-fill" style={{color: '#FF6B35'}}></i>
                  </motion.div>
                  <motion.div 
                    className="stethoscope-small-2"
                    animate={{ y: [-10, 10] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <i className="bi-activity" style={{color: '#FF8F00'}}></i>
                  </motion.div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Key Features Section */}
      <section className="features-section py-5">
        <Container>
          <motion.div
            ref={featuresRef}
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="section-header text-center mb-5">
              <h2 className="section-title">Key Features of NetSuite</h2>
              <p className="section-description">
                Comprehensive healthcare capabilities designed for US healthcare providers
              </p>
            </motion.div>
            
            <Row className="g-4">
              {keyFeatures.map((feature, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ y: -10, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Card className="feature-card h-100 border-0">
                      <Card.Body className="p-4">
                        <motion.div 
                          className="feature-icon mb-3"
                          style={{ backgroundColor: `${feature.color}15`, color: feature.color }}
                          whileHover={{ rotate: 360, scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <i className={`bi ${feature.icon}`}></i>
                        </motion.div>
                        <h5 className="feature-title mb-3">{feature.title}</h5>
                        <p className="feature-description">{feature.description}</p>
                        
                        <motion.div
                          className="feature-accent"
                          style={{ background: feature.color }}
                          initial={{ width: 0 }}
                          whileInView={{ width: "60px" }}
                          transition={{ duration: 0.8, delay: index * 0.1 }}
                        />
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section py-5">
        <Container>
          <motion.div
            ref={benefitsRef}
            initial="hidden"
            animate={benefitsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row className="mb-5">
              <Col lg={12}>
                <motion.div variants={slideInLeft} className="text-center">
                  <h2 className="section-title">Who Benefits from NetSuite for Healthcare Industry</h2>
                  <p className="section-description">
                    Discover how healthcare organizations across the USA leverage NetSuite to improve 
                    patient care, ensure compliance, and achieve operational excellence.
                  </p>
                </motion.div>
              </Col>
            </Row>
            
            <Row className="g-4">
              {benefits.map((benefit, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="h-100"
                  >
                    <Card className="benefit-card h-100 border-0">
                      <Card.Body className="p-4 text-center">
                        <motion.div 
                          className="benefit-icon mb-3"
                          whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                        >
                          <i className={`bi ${benefit.icon}`}></i>
                        </motion.div>
                        <h5 className="benefit-title mb-3">{benefit.title}</h5>
                        <p className="benefit-description mb-3">{benefit.description}</p>
                        <Badge className="stats-badge">{benefit.stats}</Badge>
                      </Card.Body>
                      
                      <motion.div
                        className="card-accent"
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                      />
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Process Benefits Section */}
      <section className="process-section py-5">
        <Container>
          <motion.div
            ref={processRef}
            initial="hidden"
            animate={processInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="section-header text-center mb-5">
              <h2 className="section-title">Transform Your Healthcare Operations</h2>
            </motion.div>
            
            <Row className="g-4">
              {processSteps.map((step, index) => (
                <Col lg={3} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ y: -15 }}
                    className="process-card text-center"
                  >
                    <motion.div 
                      className="process-icon mb-4"
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <i className={`bi ${step.icon}`}></i>
                    </motion.div>
                    <h5 className="process-title mb-3">{step.title}</h5>
                    <p className="process-description">{step.description}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="cta-content">
              <motion.div
                className="cta-icon mb-4"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <i className="bi-heart-pulse-fill"></i>
              </motion.div>
              <h2 className="cta-title mb-4">Ready to Transform Your Healthcare Organization?</h2>
              <p className="cta-description mb-4">
                Join hundreds of healthcare providers who have revolutionized their operations with NetSuite ERP
              </p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Button className="btn-primary-custom btn-lg px-5 py-3">
                    <i className="bi-chat-dots me-2"></i>
                    Get Started Today
                  </Button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <FAQSection />
    </div>
  );
};

export default HealthcarePage;